import React, { useEffect, useState, useRef } from 'react';
import { listAll, getDownloadURL, getMetadata, ref, deleteObject, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaFilePdf, FaFileWord, FaFileExcel, FaFileAlt, FaFileImage, 
  FaFileArchive, FaFileCode, FaFileAudio, FaFileVideo, 
  FaTrash, FaDownload, FaUpload, FaEllipsisV 
} from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';

export default function FileManager() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const getCompanyId = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().companyId) {
          return userDoc.data().companyId;
        }
        throw new Error('No company associated with this user');
      } catch (err) {
        console.error('Error getting company ID:', err);
        setError('Failed to load company information');
        return null;
      }
    };

    const listFiles = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      
      try {
        const companyId = await getCompanyId();
        if (!companyId) return;
        
        setCompanyId(companyId);
        const allFiles = [];

        // Only check the uploads folder
        try {
          const uploadsRef = ref(storage, `companies/${companyId}/uploads`);
          const uploadsList = await listAll(uploadsRef);

          for (const item of uploadsList.items) {
            const url = await getDownloadURL(item);
            const metadata = await getMetadata(item);
            allFiles.push({
              name: item.name,
              url,
              fullPath: item.fullPath,
              size: metadata.size || 0,
              contentType: metadata.contentType,
              folder: 'uploads'
            });
          }
        } catch (err) {
          console.warn('No files found in uploads folder or error loading:', err);
        }

        setFiles(allFiles);
      } catch (err) {
        console.error(err);
        setError('Failed to load files.');
      } finally {
        setLoading(false);
      }
    };

    listFiles();
  }, [user]);

  const handleDelete = async (file) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete "${file.name}"?`);
    if (!confirmDelete) return;

    try {
      const fileRef = ref(storage, file.fullPath);
      await deleteObject(fileRef);

      setFiles(prev => prev.filter(f => f.fullPath !== file.fullPath));
      setMessage(`${file.name} deleted successfully.`);
      setError('');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to delete file:', err);
      setError(`Failed to delete ${file.name}.`);
      setMessage('');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleFileChange = async (e) => {
    setIsUploading(true);
    const file = e.target.files[0];
    const storageRef = ref(storage, `companies/${companyId}/uploads/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed', (snapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      setUploadProgress(progress);
    }, (err) => {
      console.error('Error uploading file:', err);
      setError('Failed to upload file.');
      setIsUploading(false);
    }, async () => {
      try {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        const metadata = await getMetadata(uploadTask.snapshot.ref);
        setFiles(prev => [...prev, {
          name: file.name,
          url,
          fullPath: uploadTask.snapshot.ref.fullPath,
          size: metadata.size || 0,
          contentType: metadata.contentType,
          folder: 'uploads'
        }]);
        setMessage('File uploaded successfully.');
        setError('');
        setIsUploading(false);
        setTimeout(() => setMessage(''), 3000);
      } catch (err) {
        console.error('Error getting uploaded file metadata:', err);
        setError('Failed to get uploaded file metadata.');
        setIsUploading(false);
      }
    });
  };

  const handlePreview = (file, e) => {
    // Don't show preview if the click was on a button or link
    if (e?.target?.closest('button, a')) {
      return;
    }
    setPreviewFile(file);
  };

  const filteredFiles = files
    .filter(file => file.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => sortBy === 'size' ? b.size - a.size : a.name.localeCompare(b.name));

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <FaFileImage className="text-purple-500" />;
    if (contentType === 'application/pdf') return <FaFilePdf className="text-red-500" />;
    if (contentType?.includes('word')) return <FaFileWord className="text-blue-600" />;
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) 
      return <FaFileExcel className="text-green-600" />;
    if (contentType?.startsWith('audio/')) return <FaFileAudio className="text-purple-500" />;
    if (contentType?.startsWith('video/')) return <FaFileVideo className="text-purple-400" />;
    if (contentType?.includes('zip') || contentType?.includes('compressed')) 
      return <FaFileArchive className="text-yellow-500" />;
    if (contentType?.includes('text/') || contentType?.includes('javascript') || contentType?.includes('json')) 
      return <FaFileCode className="text-gray-400" />;
    return <FaFileAlt className="text-gray-500" />;
  };

  const getFileType = (contentType, fileName) => {
    if (contentType?.startsWith('image/')) return 'Image';
    if (contentType === 'application/pdf') return 'PDF';
    if (contentType?.includes('word')) return 'Word';
    if (contentType?.includes('excel') || contentType?.includes('spreadsheet')) return 'Excel';
    if (contentType?.startsWith('audio/')) return 'Audio';
    if (contentType?.startsWith('video/')) return 'Video';
    if (contentType?.includes('zip') || contentType?.includes('compressed')) return 'Archive';
    if (contentType?.includes('text/') || contentType?.includes('javascript') || 
        contentType?.includes('json') || contentType?.includes('xml')) {
      return fileName.split('.').pop().toUpperCase();
    }
    return 'File';
  };

  const renderPreviewModal = () => {
    if (!previewFile) return null;

    const isImage = previewFile.contentType?.startsWith('image/');
    const fileType = getFileType(previewFile.contentType, previewFile.name);
    
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setPreviewFile(null)}>
        <div className="bg-[#1a1a1a] rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-gray-800 shadow-2xl" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-[#1f1f1f]">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex-shrink-0 p-2 bg-[#2a2a2a] rounded-lg">
                {getFileIcon(previewFile.contentType)}
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-medium text-white truncate">{previewFile.name}</h3>
                <p className="text-xs text-gray-400 truncate">{fileType} • {formatSize(previewFile.size)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={previewFile.url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                onClick={(e) => e.stopPropagation()}
                title="Download"
              >
                <FaDownload className="w-4 h-4" />
              </a>
              <a
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                onClick={(e) => {e.stopPropagation(); handleDelete(previewFile); setPreviewFile(null)}}
                title="Delete"
              >
                <FaTrash className="w-4 h-4" />
              </a>
              <button
                onClick={() => setPreviewFile(null)}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Close preview"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Preview Content */}
          <div className="flex-1 flex items-center justify-center p-6 md:p-8 overflow-auto bg-gradient-to-br from-[#1a1a1a] to-[#141414]">
            {isImage ? (
              <img 
                src={previewFile.url} 
                alt={previewFile.name}
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="text-center p-8">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl bg-[#2a2a2a] text-5xl mb-6 text-purple-400">
                  {getFileIcon(previewFile.contentType)}
                </div>
                <h4 className="text-lg font-medium text-white mb-2">No Preview Available</h4>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  We can't display a preview for this {fileType.toLowerCase()} file.
                </p>
                <a 
                  href={previewFile.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaDownload className="text-sm" /> Download File
                </a>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-4 border-t border-gray-800 bg-[#1f1f1f]">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-1">File Name</p>
                <p className="text-gray-300 font-medium truncate">{previewFile.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">File Type</p>
                <p className="text-gray-300 font-medium">{fileType}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">File Size</p>
                <p className="text-gray-300 font-medium">{formatSize(previewFile.size)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-3xl text-white font-bold mb-6">File Manager</h2>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search files..."
              className="w-full px-4 py-2.5 rounded-xl bg-[#1f1f1f] text-white border border-gray-700 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none transition-all duration-200"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg 
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl flex items-center justify-center gap-2 font-medium transition-colors"
          >
            <FaUpload className="text-sm" /> Upload
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      {isUploading && (
        <div className="mb-6 p-4 bg-[#1f1f1f] rounded-xl border border-gray-800">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium text-gray-300">Uploading file...</span>
            <span className="text-sm font-medium text-gray-400">{Math.round(uploadProgress)}%</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="w-full flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
          <span className="text-white">Loading files...</span>
        </div>
      ) : files.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-700 rounded-2xl bg-[#1a1a1a] text-center">
          <div className="w-16 h-16 bg-[#2a2a2a] rounded-full flex items-center justify-center mb-4">
            <FaFileAlt className="text-2xl text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No files uploaded yet</h3>
          <p className="text-gray-400 mb-6 max-w-md">
            Upload your first file by clicking the button above or drag and drop files here
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors"
          >
            Upload Files
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredFiles.map(file => (
            <div 
              key={file.fullPath} 
              className="group bg-[#1a1a1a] rounded-xl p-4 border border-gray-800 hover:border-purple-500/30 hover:bg-[#222] transition-all duration-200 cursor-pointer shadow-sm hover:shadow-purple-500/10"
              onClick={(e) => handlePreview(file, e)}
            >
              <div className="flex flex-col h-full">
                <div className="flex-grow flex flex-col items-center justify-center p-3">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#2a2a2a] rounded-xl mb-3 group-hover:bg-[#333] transition-colors">
                    <div className="text-3xl">
                      {getFileIcon(file.contentType)}
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-center text-white truncate w-full px-2">{file.name}</h3>
                  <div className="flex gap-2 mt-2 text-xs text-gray-400">
                    <span className="px-2 py-0.5 bg-[#2a2a2a] rounded-full">
                      {getFileType(file.contentType, file.name)}
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-400">{formatSize(file.size)}</span>
                  </div>
                </div>
                <div className="flex justify-center gap-3 pt-3 mt-3 border-t border-gray-800">
                  <a 
                    href={file.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-blue-400 rounded-lg hover:bg-[#2a2a2a] transition-colors"
                    title="Download"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaDownload className="w-4 h-4" />
                  </a>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(file);
                    }}
                    className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-[#2a2a2a] transition-colors"
                    title="Delete"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {renderPreviewModal()}
    </div>
  );
}